import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import Lock from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import { IconButton, InputAdornment, TextField, TextFieldProps } from '@mui/material';

import { Constants } from '@arrived/common';
import { TaxIdType } from '@arrived/models';

import { CustomInputProps, TaxIdInput, TaxIdInputProps } from './TaxIdInput';

export const ArrivedTaxIdInput = ({
  isOptional,
  taxIdType,
  TextFieldProps,
  ...rest
}: Omit<TaxIdInputProps, 'customInput' | 'hiddenCharacter' | 'show'> & {
  isOptional?: boolean;
  TextFieldProps?: TextFieldProps;
}) => {
  const [isShowing, setIsShowing] = useState(false);

  const toggleIsShowing = useCallback(() => setIsShowing((isShowing) => !isShowing), []);

  const taxIdLabel = useMemo(() => {
    return taxIdType === TaxIdType.SSN ? (
      <FormattedMessage id="ssn" defaultMessage="SSN" />
    ) : (
      <FormattedMessage id="ein" defaultMessage="EIN" />
    );
  }, [taxIdType]);

  const CustomInput = useCallback(
    (props: CustomInputProps) => {
      return (
        <TextField
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Lock />
                <IconButton onClick={toggleIsShowing}>
                  <Visibility />
                </IconButton>
              </InputAdornment>
            ),
          }}
          label={
            isOptional ? (
              <FormattedMessage
                id="tax-id-optional-label"
                defaultMessage="{taxIdLabel} (optional)"
                values={{ taxIdLabel }}
              />
            ) : (
              taxIdLabel
            )
          }
          helperText={
            isOptional ? (
              <FormattedMessage
                id="tax-id-requirement-optional-helper-text"
                defaultMessage="If you have a {taxIdLabel}, enter it here"
                values={{ taxIdLabel }}
              />
            ) : null
          }
          type="tel"
          {...props}
        />
      );
    },
    [taxIdType, toggleIsShowing]
  );

  return (
    <TaxIdInput
      {...rest}
      customInput={CustomInput}
      CustomInputProps={TextFieldProps}
      hiddenCharacter={Constants.bullet}
      show={isShowing}
      taxIdType={taxIdType}
    />
  );
};
